/**
 * The browser side half of `admin/src/server/apiWrapper/handleError.ts`.
 *
 * Every API route answers a failure with the same JSON body, so the browser can
 * rebuild that body as a real Error and hand the same detail to whatever is
 * showing it - a table, a form, a modal - instead of flattening it into a bare
 * `new Error(message)` that loses the status and the timestamp.
 */
export type PillarErrorResponseBody = {
  isSuccessful: false;
  /** Already trimmed by `handleError` to what this caller is allowed to see. */
  message?: string;
  /** The server stack. Only sent to society admins and outside of prod. */
  stack?: string;
  /** ISO timestamp the server stamped the failure with. */
  timestamp?: string;
};

/** What we show when the server said nothing useful, or nothing at all. */
export const DEFAULT_PILLAR_API_ERROR_MESSAGE =
  "Something went wrong. Please try again.";

/** No response ever arrived - the request was aborted or the network dropped. */
export const NO_RESPONSE_STATUS_CODE = 0;

export type PillarApiErrorProps = {
  message?: string;
  /** HTTP status of the response. Defaults to {@link NO_RESPONSE_STATUS_CODE}. */
  statusCode?: number;
  /** The `stack` field of the response body, when the caller was allowed one. */
  serverStack?: string;
  /** The response body's `timestamp`. Defaults to when we built this error. */
  timestamp?: string;
  /** Whatever was originally thrown, when this error was built from one. */
  originalError?: unknown;
};

/**
 * A failed API response as an Error.
 *
 * The fields mirror the body written by `handleError`, so nothing has to know
 * how a failure is shaped beyond this class.
 */
export class PillarApiError extends Error {
  readonly statusCode: number;
  /**
   * The `stack` from the response body. Kept under its own name because `stack`
   * is already the browser's stack for this error, and overwriting it would
   * lose where the request was made from.
   */
  readonly serverStack?: string;
  readonly timestamp: string;
  readonly originalError?: unknown;

  constructor({
    message,
    statusCode = NO_RESPONSE_STATUS_CODE,
    serverStack,
    timestamp,
    originalError,
  }: PillarApiErrorProps) {
    super(message || DEFAULT_PILLAR_API_ERROR_MESSAGE);
    this.name = "PillarApiError";
    this.statusCode = statusCode;
    this.serverStack = serverStack;
    this.timestamp = timestamp ?? new Date().toISOString();
    this.originalError = originalError;
    // Restores the prototype chain so `instanceof PillarApiError` works after
    // the class is transpiled down to ES5.
    Object.setPrototypeOf(this, PillarApiError.prototype);
  }
}

/** True when the body looks like the one `handleError` writes. */
export const isPillarErrorResponseBody = (
  body: unknown,
): body is PillarErrorResponseBody =>
  !!body &&
  typeof body === "object" &&
  "isSuccessful" in body &&
  (body as { isSuccessful?: unknown }).isSuccessful === false;

/**
 * Builds the error for a failed response.
 *
 * A body of our own shape supplies the message. A body of some other shape is
 * dumped as-is, because whatever it says about the failure beats saying
 * nothing. `fallbackMessage` is for when there was no readable body at all.
 */
export const pillarApiErrorFromResponse = ({
  statusCode,
  body,
  fallbackMessage,
}: {
  statusCode: number;
  body?: unknown;
  fallbackMessage?: string;
}): PillarApiError => {
  const errorBody = isPillarErrorResponseBody(body) ? body : undefined;
  return new PillarApiError({
    message:
      errorBody?.message ??
      (body === undefined ? undefined : JSON.stringify(body)) ??
      fallbackMessage,
    statusCode,
    serverStack: errorBody?.stack,
    timestamp: errorBody?.timestamp,
    originalError: body,
  });
};

/**
 * Anything thrown - one of ours, a network `TypeError`, a string - as a
 * `PillarApiError`, so code that displays a failure has one shape to read.
 */
export const toPillarApiError = (error: unknown): PillarApiError => {
  if (error instanceof PillarApiError) {
    return error;
  }
  if (error instanceof Error) {
    return new PillarApiError({
      message: error.message,
      originalError: error,
    });
  }
  return new PillarApiError({
    message: typeof error === "string" ? error : undefined,
    originalError: error,
  });
};
